import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import { basicCustomInsightConsts } from 'actions/modules/insight';
import { routing } from 'routing';

import { createSelector } from 'reselect';

const EMPTY_OBJECT = {},
      EMPTY_ARRAY = []

export default (() => {
  const getCustomState = state => state.insightState.customState
  const useFromCustomState = paramName => useSelector(state => getCustomState(state)[paramName])

  const getInsightContent = state => getCustomState(state).content || EMPTY_OBJECT;
  const useFromInsightContent = paramName => useSelector(state => getInsightContent(state)[paramName])
  
  const getInsightInfo = state => getCustomState(state).info || EMPTY_OBJECT;
  const useFromInsightInfo = paramName => useSelector(state => getInsightInfo(state)[paramName])

  const getInsightTiles = state => getInsightContent(state).tiles || EMPTY_ARRAY
  const getInsightTile = ({ state, tileIndex }) => getInsightTiles(state)[tileIndex]
  const getTileIndexById = (tiles, tileId) => tiles.findIndex(tile => tile.id === tileId)

  const getInsightTileOverwrite = ({ state, tileIndex }) => {
    const tileId = getInsightTile({ state, tileIndex })?.id
    return getCustomState(state)?.tileOverwrite?.[tileId]
  }

  const getTilePathValue = ({ source, paramName, fieldName, arrayIndex }) => {
    if(arrayIndex !== undefined) {
      return source?.[paramName]?.[arrayIndex]?.[fieldName]
    }

    if(fieldName !== undefined) {
      return source?.[paramName]?.[fieldName]
    }

    return source?.[paramName]
  }

  const getTileDefaultValue = ({ defaultValue, paramName, fieldName, tileType }) => {
    if(defaultValue !== undefined) {
      return defaultValue
    }

    if(fieldName !== undefined) {
      return basicCustomInsightConsts.getParamDefaultValue({ paramName: fieldName, type: tileType })
    }

    return basicCustomInsightConsts.getParamDefaultValue({ paramName, type: tileType })
  }

  const getTileMappedValue = ({ value, mapperParamName, tileType, useMapping }) => {
    if(!useMapping) {
      return value
    }

    const mapper = basicCustomInsightConsts.getParamMapper({ paramName: mapperParamName, type: tileType })
    return mapper ? mapper(value) : value
  }

  const createTileIndexByIdSelector = tileId => createSelector([getInsightTiles], tiles => getTileIndexById(tiles, tileId))
  const createResolvedInsightTileValueSelector = ({ tileIndex, paramName, fieldName, arrayIndex, defaultValue, useMapping = false }) => createSelector([state => getInsightTile({ state, tileIndex }), state => getInsightTileOverwrite({ state, tileIndex })], (tile, tileOverwrite) => {
    const tileId = tile?.id,
          tileType = tile?.type,
          overwrittenValue = getTilePathValue({ source: tileOverwrite, paramName, fieldName, arrayIndex })

    if(overwrittenValue !== undefined && tileId !== undefined) {
      return overwrittenValue
    }

    const localDefaultValue = getTileDefaultValue({ defaultValue, paramName, fieldName, tileType }),
          value = getTilePathValue({ source: tile, paramName, fieldName, arrayIndex }),
          resolvedValue = value === undefined ? localDefaultValue : value

    return getTileMappedValue({ value: resolvedValue, mapperParamName: fieldName !== undefined ? fieldName : paramName, tileType, useMapping })
  })

  const useResolvedInsightTileValue = ({ tileIndex, paramName, fieldName, arrayIndex, defaultValue, useMapping = false }) => {
    const selector = useMemo(() => createResolvedInsightTileValueSelector({ tileIndex, paramName, fieldName, arrayIndex, defaultValue, useMapping }), [arrayIndex, defaultValue, fieldName, paramName, tileIndex, useMapping])
    return useSelector(selector)
  }

  const useFromTilePathById = ({ tileId, tileHook, ...rest }) => {
    const tileIndexSelector = useMemo(() => createTileIndexByIdSelector(tileId), [tileId]),
          tileIndex = useSelector(tileIndexSelector)
    return tileHook({ tileIndex, ...rest })
  }

  const useFromInsightTileList = ({ tileIndex, listName, listIndex, paramName, defaultValue }) => {
    const value = useSelector(state => paramName ?
      getInsightContent(state).tiles[tileIndex]?.[listName]?.[listIndex]?.[paramName] 
      :
      getInsightContent(state).tiles[tileIndex]?.[listName]?.[listIndex]
    )

    return value !== undefined ? value : defaultValue
  }

  const useInsightVersion = () => {
    const version = useFromInsightInfo("version")
    return version
  }

  const useInsightFileName = () => {
    const rawFileName = useFromInsightInfo("fileName") || ""
    return rawFileName.replace(/.json/g, ""); 
  }

  const useInsightGroupName = () => {
    const groupName = useFromInsightInfo("groupName")
    return groupName || ""
  }

  const useIsPublic = () => {
    const fields = useFromInsightInfo("fields") || EMPTY_ARRAY,
          isPublic = useMemo(() => fields.find(field => field.name === basicCustomInsightConsts.INSIGHT_FILE_FIELDS.PUBLIC_VIEW)?.value === "true", [fields])
    return isPublic;
  }

  const useIsPublicDirectory = () => {
    const { pathname } = useLocation(),
          isPublicDirectory = useMemo(() => String(pathname).startsWith(`/${routing.public.name}`), [pathname])
    return isPublicDirectory;
  }

  const useHasProdTag = () => {
    const fields = useFromInsightInfo("fields"),
          tags = useMemo(() => fields?.find(field => field.name === basicCustomInsightConsts.INSIGHT_FILE_FIELDS.TAGS)?.value?.split(" ").map(v => decodeURI(v)), [fields]),
          isProd = useMemo(() => tags?.findIndex(t => String(t).toLowerCase() === "prod"), [tags])
    return isProd > -1
  }

  const useIsReportView = () => {
    const [searchParams] = useSearchParams(),
          reportView = useMemo(() => searchParams.get("reportView") === "true", [searchParams])
    return reportView; 
  }

  const useFromInsightTile = ({ tileIndex, paramName, defaultValue, useMapping = false }) => useResolvedInsightTileValue({ tileIndex, paramName, defaultValue, useMapping })

  const useFromTileOverwrite = ({ tileIndex, paramName, defaultValue }) => {
    const tileId = useFromInsightTile({ tileIndex, paramName: "id" })
    const value = useFromTileOverwriteById({ tileId, paramName, defaultValue })
    return value
  }
  
  const useFromTileOverwriteById = ({ tileId, paramName, defaultValue }) => {
    const overwrittenValue = useSelector(state => getCustomState(state)?.tileOverwrite?.[tileId]?.[paramName])
    return overwrittenValue === undefined ? defaultValue : overwrittenValue;
  }

  const useFromInsightTileObject = ({ tileIndex, paramName, fieldName, defaultValue, useMapping = false }) => useResolvedInsightTileValue({ tileIndex, paramName, fieldName, defaultValue, useMapping })

  const useFromInsightTileArrayObject = ({ tileIndex, paramName, arrayIndex, fieldName, defaultValue, useMapping = false }) => useResolvedInsightTileValue({ tileIndex, paramName, arrayIndex, fieldName, defaultValue, useMapping })

  const getBandItemFieldValue = ({ fieldName, referenceData }) => {
    const bandItem = (referenceData || EMPTY_ARRAY).find(item => item?.type === basicCustomInsightConsts.REFERENCE_DATA_TYPES.BAND.value)
    if(!bandItem) {
      return undefined
    }
    return bandItem[fieldName] === undefined ? basicCustomInsightConsts.REFERENCE_BAND_DEFAULTS[fieldName] : bandItem[fieldName]
  }

  // The Band item has no fixed position in referenceData; returning only the field re-renders the caller when that field changes.
  const useFromReferenceBandItem = ({ tileIndex, fieldName }) => {
    const selector = useMemo(() => createSelector(
      [createResolvedInsightTileValueSelector({ tileIndex, paramName: "referenceData" })],
      referenceData => getBandItemFieldValue({ fieldName, referenceData })
    ), [fieldName, tileIndex])
    return useSelector(selector)
  }

  const useFromInputParams = ({ id, paramName, defaultValue }) => useSelector(state => getCustomState(state).inputParams?.[id]?.[paramName] || defaultValue)
  const getInputParamsConfig = ({ state, id }) => getInsightContent(state).inputParams?.[id]
  const getInputParamsPresetParam = ({ state, id, presetIndex, paramName }) => (getInputParamsConfig({ state, id })?.presets?.[presetIndex] || EMPTY_OBJECT)[paramName]
  const useFromInputParamsPresetParam = ({ id, presetIndex, paramName }) => useSelector(state => getInputParamsPresetParam({ state, id, presetIndex, paramName }))
  const useFromInputParamsConfig = ({ id, paramName, defaultValue, useMapping = false }) => {
    const activeInputParamsId = useFromCustomState("activeInputParamsId"),
          localId = id !== undefined ? id : activeInputParamsId,
          localDefaultValue = defaultValue !== undefined ? defaultValue : basicCustomInsightConsts.getParamDefaultValue({ paramName, type: basicCustomInsightConsts.TILE_TYPES.INPUT_PARAMS })
    return useSelector(state => {
      const value = getInputParamsConfig({ state, id: localId })?.[paramName]
      const resolvedValue = value === undefined ? localDefaultValue : value
      if(useMapping) {
        const mapper = basicCustomInsightConsts.getParamMapper({ paramName, type: basicCustomInsightConsts.TILE_TYPES.INPUT_PARAMS })
        return mapper ? mapper(resolvedValue) : resolvedValue
      }
      return resolvedValue
    })
  }

  const useFromActiveBasket = paramName => {
    const activeBasketId = useFromCustomState("activeBasketId"),
          value = useSelector(state => getCustomState(state).basket?.[activeBasketId]?.[paramName])
    return value
  }

  const useFromActiveBasketWithId = ({ paramName, activeBasketId, defaultValue }) => {
    const value = useSelector(state => getCustomState(state).basket?.[activeBasketId]?.[paramName]) || defaultValue
    return value
  }

  const getTileById = (state, id) => state.insightState.customState.content?.tiles?.find(t => t.id === id)
  const useTileById = ({ tileId }) => {
    const tiles = useSelector(getInsightTiles),
          tile = useMemo(() => tiles.find(t => t.id === tileId), [tileId, tiles])
    return tile
  }
  const useFromTileById = ({ paramName, tileId, defaultValue, ...rest }) => useFromTilePathById({ tileId, tileHook: useFromInsightTile, paramName, defaultValue, ...rest })
  const useFromTileObjectById = ({ tileId, ...rest }) => useFromTilePathById({ tileId, tileHook: useFromInsightTileObject, ...rest })
  const useFromTileArrayObjectById = ({ tileId, ...rest }) => useFromTilePathById({ tileId, tileHook: useFromInsightTileArrayObject, ...rest })

  const useFromPanelUserSelection = paramName => {
    const activeInputParamsId = useFromCustomState("activeInputParamsId"),
          value = useSelector(state => getCustomState(state).inputParamsUserSelection?.[activeInputParamsId]?.[paramName])
    return value
  }

  const useFromEmailExportState = paramName => useSelector(state => getCustomState(state).emailExportState[paramName])

  const useIsDraft = () => {
    const fields = useFromInsightInfo("fields"),
          isDraft = fields?.find(field => field.name === basicCustomInsightConsts.INSIGHT_FILE_FIELDS.DRAFT)?.value
    return isDraft === true || isDraft === "true"
  }

  return {
    getInsightContent,
    getInsightInfo,
    useFromCustomState,
    useFromInsightContent,
    useFromInsightInfo,
    useInsightFileName,
    useInsightGroupName,
    useIsPublic,
    useIsPublicDirectory,
    useFromInsightTile,
    useFromInsightTileObject,
    useFromInsightTileArrayObject,
    useFromReferenceBandItem,
    useFromInputParams,
    useFromInputParamsConfig,
    useFromInputParamsPresetParam,
    getInputParamsConfig,
    useFromActiveBasket,
    useFromActiveBasketWithId,
    getTileById,
    useTileById,
    useFromTileById,
    useFromPanelUserSelection,
    useFromEmailExportState,
    useFromInsightTileList,
    useIsReportView,
    useInsightVersion,
    useFromTileOverwrite,
    useFromTileOverwriteById,
    useFromTileObjectById,
    useFromTileArrayObjectById,
    useHasProdTag,
    useIsDraft
  }
})()
